<template>
  <div
    id="app_content"
    :class="{ 'has-recent-keywords': showRecentKeywordBar }"
  >
    <best-city
      ref="vuecity"
      slide="horizontal"
      backStyle="tilted"
      :location="locatedCity"
      @closeChooseCity="getCity"
      @locationError="handleCityLocationError"
    />

    <div class="title">选择餐厅</div>
    <div class="container">
      <select-city
        :selectCity="searchKey"
        @openCity="showCity"
      />
      <search-store
        ref="searchStoreRef"
        :disInput="disInput1"
        :clear-text="clearSearch"
        @click="showToast"
        @txtdata="searchText"
      />
    </div>

    <div v-if="showRecentKeywordBar" class="recent-keyword-section">
      <span>最近搜索</span>
      <div class="recent-keyword-list">
        <button
          v-for="keyword in recentStoreKeywords"
          :key="'recent-keyword-' + keyword"
          type="button"
          @click="applyRecentKeyword(keyword)"
        >{{ keyword }}</button>
      </div>
    </div>

    <div class="scroll2">
      <h3 v-if="nearbyTitle" class="nearby-title">{{ nearbyTitle }}</h3>
      <div class="store-scroll-body">
        <scroll
          ref="suggest11"
          :data="scrollData"
          :probe-type="3"
          :listen-scroll="true"
          event-passthrough="horizontal"
          @distance="setScrollDistance"
          @click="handleScrollClick"
        >
          <div class="list-content">
            <div v-if="showRecentQuickStores" class="recent-section">
              <div class="recent-header">
                <strong>最近使用</strong>
                <span>可查看复制</span>
              </div>
              <div class="recent-list">
                <button
                  v-for="store in recentQuickStores"
                  :key="'recent-' + getRecentStoreKey(store)"
                  type="button"
                  class="recent-card"
                  @click="openStoreDetail(store)"
                >
                  <strong>{{ store.storename }}</strong>
                  <span>{{ recentStoreMeta(store) }}</span>
                  <small v-if="store.address">{{ store.address }}</small>
                </button>
              </div>
            </div>
            <template v-if="isKeywordResults">
              <section class="restaurant-results">
                <h3 class="result-heading">餐厅（{{ visibleStores.length }}家餐厅）</h3>
                <p v-if="storeLoading" class="result-state">正在搜索餐厅...</p>
                <p v-else-if="storeError" class="result-state result-error">{{ storeError }}</p>
                <p v-else-if="visibleStores.length === 0" class="result-state">未找到相关餐厅</p>
                <store-list
                  v-else
                  :storelist="visibleStores"
                  :showStoreCity="isShowStoreCity"
                  :showStatus="true"
                  :showDistance="true"
                  :keyword="keyWord"
                  @openStore="openStoreDetail"
                />
              </section>
              <place-list
                :places="places"
                :loading="placeLoading"
                :error="placeError"
                :keyword="keyWord"
                @selectPlace="selectPlace"
              />
            </template>
            <template v-else-if="showBrowseSection">
              <section
                class="browse-results"
                :aria-busy="browseStatus === 'loading' ? 'true' : 'false'"
              >
                <h3 v-if="browseSectionTitle" class="browse-heading">{{ browseSectionTitle }}</h3>
                <div
                  v-if="browseStatus !== 'success'"
                  :class="['browse-state', `browse-state-${browseStatus}`]"
                  :role="browseStatusIsError ? 'alert' : 'status'"
                  aria-live="polite"
                >
                  <i v-if="browseStatus === 'loading'" class="browse-state-spinner" aria-hidden="true"></i>
                  <strong>{{ browseStatusTitle }}</strong>
                  <p>{{ browseStatusMessage }}</p>
                  <div v-if="browsePrimaryActionText" class="browse-state-actions">
                    <button
                      type="button"
                      class="browse-action browse-action-primary"
                      :disabled="locating || storeLoading"
                      @click.stop="handleBrowsePrimaryAction"
                    >{{ browsePrimaryActionText }}</button>
                    <button
                      v-if="browseSecondaryActionText"
                      type="button"
                      class="browse-action browse-action-secondary"
                      :disabled="locating || storeLoading"
                      @click.stop="showCity"
                    >{{ browseSecondaryActionText }}</button>
                  </div>
                </div>
                <store-list
                  v-else
                  :storelist="visibleStores"
                  :showStoreCity="isShowStoreCity"
                  :showStatus="true"
                  :showDistance="true"
                  :keyword="keyWord"
                  @openStore="openStoreDetail"
                />
                <p v-if="showNearbyLimit" class="nearby-limit">已为您展示附近{{ nearbyRadiusKilometers }}km餐厅</p>
              </section>
            </template>
            <template v-else>
              <div v-if="showEmpty" class="empty-state">{{ emptyText }}</div>
              <store-list
                v-else
                :storelist="visibleStores"
                :showStoreCity="isShowStoreCity"
                :showStatus="true"
                :showDistance="true"
                :keyword="keyWord"
                @openStore="openStoreDetail"
              />
            </template>
          </div>
        </scroll>
      </div>
    </div>
  </div>
</template>

<script setup>
import BestCity from './BestCity.vue';
import SelectCity from './components/SelectCity.vue';
import SearchStore from './components/SearchStore.vue';
import Scroll from './base/Scroll.vue';
import PlaceList from './components/PlaceList.vue';
import StoreList from './components/StoreList.vue';
import { useStoreQuery } from './composables/useStoreQuery';

defineOptions({name: 'app_content'});

const {
  applyRecentKeyword,
  browsePrimaryActionText,
  browseSecondaryActionText,
  browseSectionTitle,
  browseStatus,
  browseStatusIsError,
  browseStatusMessage,
  browseStatusTitle,
  clearSearch,
  disInput1,
  emptyText,
  getCity,
  getRecentStoreKey,
  handleCityLocationError,
  handleBrowsePrimaryAction,
  handleScrollClick,
  isKeywordResults,
  isShowStoreCity,
  keyWord,
  locatedCity,
  locating,
  nearbyRadiusKilometers,
  nearbyTitle,
  openStoreDetail,
  placeError,
  placeLoading,
  places,
  recentQuickStores,
  recentStoreKeywords,
  recentStoreMeta,
  scrollData,
  searchKey,
  searchStoreRef,
  searchText,
  selectPlace,
  setScrollDistance,
  showCity,
  showBrowseSection,
  showEmpty,
  showNearbyLimit,
  showRecentQuickStores,
  showRecentKeywordBar,
  showToast,
  storeError,
  storeLoading,
  suggest11,
  visibleStores,
  vuecity
} = useStoreQuery();
</script>

<style lang="stylus">
p
  text-align start

#app_content
  font-family 'Avenir', Helvetica, Arial, sans-serif
  -webkit-font-smoothing antialiased
  -moz-osx-font-smoothing grayscale
  color var(--text-normal)
  height 100%
  width 100%
  font-size 14px
  position absolute
  top 0px
  left 0px
  z-index 1000000000000
  background var(--page-bg)

  .title
    display flex
    align-items center
    justify-content center
    text-align center
    margin-top 0.5em
    font-size 21px
    color var(--text-main)

  .container
    display flex
    align-items stretch
    margin-left 3%
    width 94%
    height 38px
    margin-top 10px
    margin-bottom 10px

  .recent-keyword-section
    width 94%
    margin 0 0 8px 3%
    display flex
    align-items center
    gap 8px
    min-height 28px
    box-sizing border-box
    overflow hidden

  .recent-keyword-section > span
    flex 0 0 auto
    color var(--text-muted)
    font-size 12px
    line-height 26px

  .recent-keyword-list
    flex 1
    min-width 0
    display flex
    gap 6px
    overflow-x auto
    -webkit-overflow-scrolling touch

  .recent-keyword-list button
    flex 0 0 auto
    max-width 150px
    height 26px
    padding 0 10px
    border 1px solid var(--border-color)
    border-radius 6px
    background var(--surface-soft)
    color var(--text-secondary)
    font-size 12px
    white-space nowrap
    overflow hidden
    text-overflow ellipsis
    cursor pointer

  .recent-keyword-list button:active
    border-color var(--accent-color)
    color var(--accent-color)
    background var(--active-bg)

  .scroll2
    display flex
    flex-direction column
    height calc(100% - 88px)
    width 94%
    margin-left 2.5%
    background var(--surface-bg)
    border 1px solid var(--border-color)
    border-radius 8px
    box-sizing border-box
    overflow hidden

  &.has-recent-keywords .scroll2
    height calc(100% - 124px)

  .store-scroll-body
    flex 1 1 auto
    min-height 0
    width 100%
    overflow hidden

  .store-scroll-body .scroll
    background var(--surface-bg)

  .list-content
    min-height 100%
    background var(--surface-bg)
    padding 4px 0
    box-sizing border-box

  .recent-section
    padding 6px 8px 4px
    border-bottom 1px solid var(--border-color)
    background var(--surface-bg)

  .recent-header
    display flex
    align-items center
    justify-content space-between
    margin-bottom 6px
    color var(--text-main)

  .recent-header strong
    font-size 14px

  .recent-header span
    font-size 12px
    color var(--text-muted)

  .recent-list
    display flex
    align-items flex-start
    gap 6px
    overflow-x auto
    padding-bottom 2px
    -webkit-overflow-scrolling touch

  .recent-card
    flex 0 0 74%
    min-width 0
    max-width 280px
    min-height 0
    border 1px solid var(--border-color)
    border-radius 7px
    background var(--page-bg)
    color var(--text-normal)
    text-align left
    padding 5px 8px
    box-sizing border-box
    cursor pointer
    display flex
    flex-direction column

  .recent-card:active
    border-color var(--accent-color)
    background var(--active-bg)

  .recent-card strong,
  .recent-card span,
  .recent-card small
    display block
    overflow hidden
    text-overflow ellipsis
    white-space nowrap

  .recent-card strong
    color var(--text-main)
    font-size 14px
    line-height 18px

  .recent-card span
    margin-top 2px
    color var(--text-secondary)
    font-size 12px
    line-height 16px

  .recent-card small
    margin-top 2px
    color var(--text-muted)
    font-size 12px
    line-height 16px

  .empty-state
    min-height 220px
    display flex
    align-items center
    justify-content center
    color var(--text-muted)
    background var(--surface-bg)
    border none
    border-radius 0
    box-sizing border-box
    margin 0

  .restaurant-results
    background var(--surface-bg)
    padding-top 4px

  .browse-results
    background var(--surface-bg)
    padding-top 4px

  .browse-heading
    margin 8px 12px 0
    color var(--text-main)
    font-size 16px
    line-height 24px

  .browse-state
    min-height 210px
    padding 28px 18px
    box-sizing border-box
    display flex
    flex-direction column
    align-items center
    justify-content center
    gap 10px
    color var(--text-normal)
    text-align center
    background var(--surface-bg)

  .browse-state strong
    color var(--text-main)
    font-size 17px
    line-height 24px

  .browse-state p
    max-width 460px
    margin 0
    color var(--text-muted)
    font-size 14px
    line-height 21px
    text-align center

  .browse-state-location-error strong,
  .browse-state-request-error strong
    color var(--accent-color)

  .browse-state-spinner
    width 26px
    height 26px
    border 3px solid var(--border-strong)
    border-top-color var(--accent-color)
    border-radius 50%
    box-sizing border-box
    animation browse-state-spin .8s linear infinite

  .browse-state-actions
    width 100%
    max-width 380px
    margin-top 6px
    display flex
    flex-wrap wrap
    justify-content center
    gap 8px

  .browse-action
    flex 1 1 128px
    max-width 180px
    min-height 42px
    padding 8px 14px
    border-radius 6px
    box-sizing border-box
    font-size 14px
    line-height 20px
    cursor pointer

  .browse-action:disabled
    opacity .6
    cursor not-allowed

  .browse-action-primary
    border 1px solid var(--accent-color)
    background var(--accent-color)
    color var(--on-accent-color)

  .browse-action-secondary
    border 1px solid var(--border-strong)
    background var(--surface-soft)
    color var(--text-secondary)

  .browse-action:not(:disabled):active
    transform translateY(1px)

  .restaurant-results > .result-heading
    margin 8px 12px
    color var(--text-main)
    font-size 16px
    line-height 24px

  .nearby-title
    flex 0 0 auto
    margin 0
    padding 8px 12px
    box-sizing border-box
    overflow-wrap anywhere
    color var(--text-main)
    font-size 16px
    line-height 24px
    background var(--surface-bg)
    border-bottom 1px solid var(--border-color)

  .result-state
    min-height 72px
    margin 0
    display flex
    align-items center
    justify-content center
    color var(--text-muted)

  .result-error
    color var(--accent-color)

  .nearby-limit
    margin 0
    padding 26px 12px 30px
    color var(--text-muted)
    font-size 14px
    line-height 20px
    text-align center
    background var(--surface-bg)

  @keyframes browse-state-spin
    to
      transform rotate(360deg)
</style>
